import React, { useState, useRef, useEffect } from 'react';

const ModerationCard = ({ 
  listing, 
  isSelected, 
  onSelect, 
  onViewDetails, 
  onAction,
  imageBaseUrl,
  activeStatus 
}) => {
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  
  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);
  
  const handleCardClick = (e) => {
    // Only select if clicking on the card background, not buttons or links
    if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    onSelect();
  };
  
  const getListingImage = () => {
    if (!listing || !isVisible) return null;
    
    // Priority 1: Use first_image_url if available
    if (listing.first_image_url && !imageError) {
      return `${imageBaseUrl}${listing.first_image_url}`;
    }
    
    // Priority 2: Use images array if available
    if (listing.images && listing.images.length > 0 && !imageError) {
      let firstImage = listing.images[0];
      if (typeof firstImage === "object") {
        firstImage = firstImage.image_url || firstImage.url || "";
      }
      if (firstImage) {
        return `${imageBaseUrl}${firstImage}`;
      }
    }
    
    // Priority 3: Check if image_url is directly on listing
    if (listing.image_url && !imageError) {
      return `${imageBaseUrl}${listing.image_url}`;
    }
    
    return null;
  };
  
  const getCategoryIcon = () => {
    if (!listing.category_name) return '📦';
    
    const category = listing.category_name.toLowerCase();
    if (category.includes('phone') || category.includes('electronic')) return '📱';
    if (category.includes('car') || category.includes('vehicle')) return '🚗';
    if (category.includes('property') || category.includes('house')) return '🏠';
    if (category.includes('service')) return '🔧';
    if (category.includes('fashion') || category.includes('cloth')) return '👕';
    if (category.includes('food')) return '🍕';
    if (category.includes('book')) return '📚';
    if (category.includes('sport')) return '⚽';
    
    return '📦';
  };
  
  const getStatusBadge = () => {
    const statusConfig = {
      pending: { 
        color: 'bg-yellow-900/30 text-yellow-400 border-yellow-800/50', 
        icon: '⏳', 
        text: 'Pending Review' 
      },
      rejected: { 
        color: 'bg-red-900/30 text-red-400 border-red-800/50', 
        icon: '❌', 
        text: 'Rejected' 
      },
      flagged: { 
        color: 'bg-[#ff660e]/20 text-[#ff660e] border-[#ff660e]/30', 
        icon: '🚩', 
        text: 'Flagged' 
      },
      approved: { 
        color: 'bg-green-900/30 text-green-400 border-green-800/50', 
        icon: '✅', 
        text: 'Approved' 
      }
    };

    const config = statusConfig[listing.moderation_status] || statusConfig.pending;
    
    return (
      <span className={`px-3 py-1.5 text-xs font-medium rounded-full border flex items-center gap-1 ${config.color}`}>
        <span>{config.icon}</span>
        <span>{config.text}</span>
      </span>
    );
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };
  
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };
  
  const imageUrl = getListingImage();
  const categoryIcon = getCategoryIcon();
  
  const getPriorityColor = () => {
    const hoursAgo = Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60));
    
    if (hoursAgo > 48) return 'bg-red-500';
    if (hoursAgo > 24) return 'bg-[#ff660e]';
    if (hoursAgo > 12) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div 
      ref={cardRef}
      className={`relative bg-gray-800 rounded-xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] overflow-hidden cursor-pointer group ${
        isSelected 
          ? 'border-[#ff660e] ring-2 ring-[#ff660e]/30 transform scale-[1.02]' 
          : 'border-gray-700 hover:border-[#ff660e]'
      }`}
      onClick={handleCardClick}
    >
      {/* Selection Overlay */}
      {isSelected && (
        <div className="absolute inset-0 bg-[#ff660e]/5 z-0"></div>
      )}
      
      {/* Selection Checkbox - Top Left */}
      <div 
        className="absolute top-4 left-4 z-10"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
        <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all duration-200 hover:scale-110 ${
          isSelected 
            ? 'bg-[#ff660e] border-[#ff660e] shadow-lg' 
            : 'bg-gray-700/90 border border-gray-600 group-hover:border-[#ff660e]'
        }`}>
          {isSelected && (
            <span className="text-white text-sm">✓</span>
          )}
        </div>
      </div>
      
      {/* Priority Indicator */}
      {activeStatus === 'pending' && (
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${getPriorityColor()} animate-pulse`}></div>
          <span className="text-xs text-gray-400 bg-gray-900/80 px-2 py-1 rounded-full font-medium">
            {getTimeAgo(listing.created_at)}
          </span>
        </div>
      )}
      
      {/* Selection Counter Badge (when selected) */}
      {isSelected && (
        <div className="absolute top-12 left-4 z-10">
          <div className="w-6 h-6 rounded-full bg-[#ff660e] flex items-center justify-center shadow-lg">
            <span className="text-white text-xs font-bold"></span>
          </div>
        </div>
      )}
      
      {/* Listing Image */}
      <div className="h-48 relative bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
        {isVisible ? (
          <>
            {imageUrl ? (
              <img 
                src={imageUrl}
                alt={listing.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                onError={() => setImageError(true)}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center group">
                <div className="text-center">
                  <div className="text-5xl mb-3 text-gray-600 group-hover:text-[#ff660e] transition-colors">{categoryIcon}</div>
                  <p className="text-sm font-medium text-gray-400">
                    {listing.image_count > 0 ? `${listing.image_count} images` : 'No images'}
                  </p>
                </div>
              </div>
            )}
            
            {/* Loading Placeholder */}
            {!imageUrl && isVisible && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </>
        ) : (
          // Loading skeleton for images not yet visible
          <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 animate-pulse flex items-center justify-center">
            <div className="text-5xl text-gray-700">{categoryIcon}</div>
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 right-3 left-3 flex flex-wrap gap-1 justify-end">
          <div className="flex flex-wrap gap-1">
            {getStatusBadge()}
          </div>
          {activeStatus !== 'pending' && (
            <span className="text-xs text-gray-400 bg-gray-900/80 px-2 py-1 rounded-full font-medium">
              {getTimeAgo(listing.created_at)}
            </span>
          )}
        </div>
      </div>
      
      {/* Listing Info */}
      <div className="p-4">
        <div className="space-y-3">
          {/* Title and Price */}
          <div>
            <h3 className="text-lg font-bold text-white mb-1 line-clamp-2 leading-tight hover:text-[#ff660e] transition-colors cursor-pointer">
              {listing.title}
            </h3>
            <p className="text-2xl font-bold text-[#ff660e] group-hover:text-orange-400 transition-colors">
              {formatCurrency(listing.price)}
            </p>
            {listing.is_negotiable && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1 group-hover:text-gray-300 transition-colors">
                <span>💬</span>
                <span>Price negotiable</span>
              </p>
            )}
          </div>

          {/* Category and Seller */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-400 group-hover:text-gray-300 transition-colors">
              <span>{categoryIcon}</span>
              <span className="font-medium">{listing.category_name}</span>
            </div>
            <div className="text-right group">
              <p className="text-sm text-white font-semibold truncate group-hover:text-[#ff660e] transition-colors">{listing.store_name}</p>
              <p className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors">{listing.seller_email}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-700 pt-2 group-hover:border-gray-600 group-hover:text-gray-400 transition-colors">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1 group-hover:text-[#ff660e] transition-colors">
                <span>👁️</span>
                <span className="font-medium">{listing.view_count || 0}</span>
              </span>
              <span className="flex items-center gap-1 group-hover:text-[#ff660e] transition-colors">
                <span>🛒</span>
                <span className="font-medium">{listing.order_count || 0}</span>
              </span>
              <span className="flex items-center gap-1 group-hover:text-[#ff660e] transition-colors">
                <span>📸</span>
                <span className="font-medium">{listing.image_count || 0}</span>
              </span>
            </div>
          </div>

          {/* Description Preview */}
          {listing.description && (
            <div className="border-t border-gray-700 pt-2 group-hover:border-gray-600 transition-colors">
              <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed group-hover:text-gray-300 transition-colors">
                {listing.description}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="px-4 py-3 bg-gray-900/50 border-t border-gray-700 group-hover:bg-gray-900/70 transition-colors">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer group/select"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            <div 
              className={`w-4 h-4 rounded border flex items-center justify-center transition-all duration-200 group-hover/select:scale-110 ${
                isSelected 
                  ? 'bg-[#ff660e] border-[#ff660e]' 
                  : 'bg-gray-700 border-gray-600 group-hover/select:border-[#ff660e]'
              }`}
            >
              {isSelected && (
                <span className="text-white text-xs">✓</span>
              )}
            </div>
            <span className="text-sm text-gray-400 group-hover/select:text-white transition-colors">Select</span>
          </div>

          <div className="flex gap-2">
            {/* Status Change Button */}
            <button
              onClick={onViewDetails}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 hover:shadow flex items-center gap-1 group-hover:scale-[1.02]"
              style={{ 
                borderColor: '#ff660e',
                color: '#ff660e',
                backgroundColor: 'transparent'
              }}
            >
              <span className="group-hover:rotate-12 transition-transform">🔍</span>
              <span className="group-hover:text-orange-400 transition-colors">View Details</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModerationCard;